/**
 * Client for the AWS Access Broker internal API.
 *
 * The broker mints short-lived, individually-attributed production AWS sessions
 * after the request is confirmed out-of-band in Slack (#dev-security-access). Requests
 * are always for the production account. Once approved, the user picks an access
 * method (Console or CLI), the broker activates the session, and hands back
 * either a console sign-in link or an `aws ssm start-session` command.
 *
 * Flip `USE_MOCK` to `false` once the broker API is deployed and BROKER is set
 * in env-config.js.
 */

import { getToken } from './api-utils'

import { config } from '@/configs/api.config'

export const AWS_ACCESS_LEVELS = {
  READ_ONLY: 'READ_ONLY',
  DEVELOPER: 'DEVELOPER',
  ADMIN: 'ADMIN'
} as const

export type AwsAccessLevel =
  (typeof AWS_ACCESS_LEVELS)[keyof typeof AWS_ACCESS_LEVELS]

export const ACCESS_METHODS = {
  CONSOLE: 'CONSOLE',
  CLI: 'CLI'
} as const

export type AwsAccessMethod =
  (typeof ACCESS_METHODS)[keyof typeof ACCESS_METHODS]

export const AWS_ACCESS_REQUEST_STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  DENIED: 'DENIED',
  EXPIRED: 'EXPIRED'
} as const

export type AwsAccessRequestStatus =
  (typeof AWS_ACCESS_REQUEST_STATUS)[keyof typeof AWS_ACCESS_REQUEST_STATUS]

export const JUMP_HOST_INSTANCE_TYPES = [
  't3.micro',
  't3.small',
  't3.medium',
  't3.large'
] as const
export type JumpHostInstanceType = (typeof JUMP_HOST_INSTANCE_TYPES)[number]

export interface CreateAwsAccessRequestInput {
  access_level: AwsAccessLevel
  duration_minutes: number
  reason: string
  /** EC2 instance type for the jump host. Only applies to READ_ONLY / DEVELOPER CLI sessions. */
  instance_type?: JumpHostInstanceType
}

export interface AwsAccessRequest extends CreateAwsAccessRequestInput {
  id: string
  status: AwsAccessRequestStatus
  requested_by: string
  approved_by?: string
  created_at: string
  /** Set once approved. */
  expires_at?: string
  /** AWS Console sign-in link — present after activating with CONSOLE method. */
  console_url?: string
  /** `aws ssm start-session ...` command — present after activating with CLI method. */
  ssm_command?: string
  instance_type?: JumpHostInstanceType
}

const USE_MOCK = false

export const IS_MOCK_BROKER = USE_MOCK

// ── Mock implementation ───────────────────────────────────────────────────────

const PROD_ACCOUNT_ID = '893804459037'

const ROLE_BY_LEVEL: Record<AwsAccessLevel, string> = {
  [AWS_ACCESS_LEVELS.READ_ONLY]: 'ep_prod_access_readonly',
  [AWS_ACCESS_LEVELS.DEVELOPER]: 'ep_prod_access_dev',
  [AWS_ACCESS_LEVELS.ADMIN]: 'ep_prod_access_admin'
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const mockStore = new Map<string, AwsAccessRequest>()

/** Mark request as approved — credentials are set later via activateRequest. */
const applyApprovedSession = (request: AwsAccessRequest) => {
  request.status = AWS_ACCESS_REQUEST_STATUS.APPROVED
  request.approved_by = request.approved_by ?? 'mock.approver@epilot.cloud'
  request.expires_at = new Date(
    Date.now() + request.duration_minutes * 60_000
  ).toISOString()
}

const newRequest = (
  input: CreateAwsAccessRequestInput,
  requestedBy: string,
  status: AwsAccessRequestStatus
): AwsAccessRequest => ({
  ...input,
  id: `req_${Math.random().toString(36).slice(2, 10)}`,
  status,
  requested_by: requestedBy,
  created_at: new Date().toISOString()
})

const mockClient = {
  createRequest: async (
    input: CreateAwsAccessRequestInput,
    requestedBy: string
  ): Promise<AwsAccessRequest> => {
    await wait(600)

    const request = newRequest(
      input,
      requestedBy,
      AWS_ACCESS_REQUEST_STATUS.PENDING
    )

    mockStore.set(request.id, request)

    // Simulate the out-of-band Slack approval landing a few seconds later.
    void wait(6000).then(() => {
      const pending = mockStore.get(request.id)

      if (pending?.status === AWS_ACCESS_REQUEST_STATUS.PENDING) {
        applyApprovedSession(pending)
      }
    })

    return request
  },

  getRequest: async (id: string): Promise<AwsAccessRequest | null> => {
    await wait(150)

    return mockStore.get(id) ?? null
  },

  activateRequest: async (
    id: string,
    accessMethod: AwsAccessMethod
  ): Promise<AwsAccessRequest> => {
    await wait(800)

    const request = mockStore.get(id)

    if (!request) throw new Error(`Request ${id} not found`)

    const role = ROLE_BY_LEVEL[request.access_level]

    if (accessMethod === ACCESS_METHODS.CONSOLE) {
      request.console_url = `https://signin.aws.amazon.com/switchrole?account=${PROD_ACCOUNT_ID}&roleName=${role}&displayName=${role}`
    } else {
      const instanceId = `i-0${Math.random().toString(16).slice(2, 12)}`

      request.ssm_command = `aws ssm start-session --target ${instanceId} --region eu-central-1`
    }

    return { ...request }
  }
}

/** Dev-only: immediately approve a pending mock request, skipping the timer. */
export const mockApproveRequest = (id: string): void => {
  const request = mockStore.get(id)

  if (request?.status === AWS_ACCESS_REQUEST_STATUS.PENDING) {
    applyApprovedSession(request)
  }
}

/** Dev-only: seed an approved-but-not-yet-activated request to preview the method choice step. */
export const mockSeedApprovedRequest = (
  requestedBy: string
): AwsAccessRequest => {
  const request = newRequest(
    {
      access_level: AWS_ACCESS_LEVELS.DEVELOPER,
      duration_minutes: 60,
      reason:
        'Incident #482: roll back failed deployment of svc-notification-api in eu-central-1.'
    },
    requestedBy,
    AWS_ACCESS_REQUEST_STATUS.PENDING
  )

  applyApprovedSession(request)
  mockStore.set(request.id, request)

  return request
}

// ── Real implementation ───────────────────────────────────────────────────────

const brokerFetch = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const token = await getToken()
  const brokerUrl = (config as Record<string, string>).BROKER

  const res = await fetch(`${brokerUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init?.headers
    }
  })

  if (!res.ok) {
    const body = await res.text()

    throw new Error(`Broker API error ${res.status}: ${body}`)
  }

  return res.json() as Promise<T>
}

const realClient = {
  createRequest: async (
    input: CreateAwsAccessRequestInput,
    requestedBy: string
  ): Promise<AwsAccessRequest> =>
    brokerFetch('/v1/access-requests', {
      method: 'POST',
      body: JSON.stringify({ ...input, requester_display: requestedBy })
    }),

  getRequest: async (id: string): Promise<AwsAccessRequest | null> => {
    try {
      return await brokerFetch(`/v1/access-requests/${id}`)
    } catch {
      return null
    }
  },

  activateRequest: async (
    id: string,
    accessMethod: AwsAccessMethod
  ): Promise<AwsAccessRequest> =>
    brokerFetch(`/v1/access-requests/${id}:activate`, {
      method: 'POST',
      body: JSON.stringify({ access_method: accessMethod })
    })
}

export const getAwsAccessBrokerClient = () =>
  USE_MOCK ? mockClient : realClient
